import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { storyService } from "@/services/story.service";

export default function StoriesRow() {
  const [stories, setStories] = useState<any[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    storyService.list().then(setStories).catch(() => {});
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const startTimer = useCallback((index: number) => {
    clearTimer();
    if (index >= (stories.length || 0)) {
      setActiveIndex(null);
      return;
    }
    timerRef.current = setTimeout(() => {
      setActiveIndex((prev) => (prev !== null ? prev + 1 : null));
    }, 20000);
  }, [stories.length, clearTimer]);

  const closeStories = useCallback(() => {
    clearTimer();
    setActiveIndex(null);
  }, [clearTimer]);

  const handleStoryClick = (story: any, index: number) => {
    if (story.link_type === "route") {
      navigate(story.link_value);
    } else if (story.link_type === "scroll") {
      document.getElementById(story.link_value)?.scrollIntoView({ behavior: "smooth" });
    } else {
      setActiveIndex(index);
    }
  };

  useEffect(() => {
    if (activeIndex !== null) startTimer(activeIndex);
    return clearTimer;
  }, [activeIndex, startTimer, clearTimer]);

  if (stories.length === 0) return null;

  const current = activeIndex !== null ? stories[activeIndex] : null;

  return (
    <section className="py-4" dir="rtl">
      <div className="overflow-x-auto scrollbar-hide">
        <div className="flex gap-4 px-4 pb-2" style={{ width: "max-content" }}>
          {stories.map((story, i) => (
            <button key={story.id} onClick={() => handleStoryClick(story, i)}
              className="flex flex-col items-center gap-2 cursor-pointer group" aria-label={story.label}>
              <div className="relative rounded-full p-[3px] transition-transform group-hover:scale-105"
                style={{
                  background: `linear-gradient(135deg, ${story.border_color || '#A78BFA'}, ${(story.border_color || '#A78BFA')}88)`,
                  boxShadow: activeIndex === i ? `0 0 0 3px ${story.border_color || '#A78BFA'}` : "none",
                }}>
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white bg-gray-100">
                  <img src={story.image} alt={story.label} className="w-full h-full object-cover object-top" loading="lazy" />
                </div>
                {story.is_new && (
                  <span className="absolute top-0 left-0 w-4 h-4 rounded-full border-2 border-white flex items-center justify-center" style={{ background: story.border_color || '#A78BFA' }}>
                    <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                  </span>
                )}
              </div>
              <span className="text-xs font-semibold text-gray-700 whitespace-nowrap max-w-[72px] text-center leading-tight">{story.label}</span>
            </button>
          ))}
        </div>
      </div>

      {current && (
        <div className="fixed inset-0 z-50 bg-black" onClick={closeStories} dir="rtl">
          {/* Progress bar */}
          <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 p-2">
            {stories.map((_, i) => (
              <div key={i} className="flex-1 h-0.5 rounded-full bg-white/30 overflow-hidden">
                <div className="h-full bg-white"
                  style={{
                    width: i < activeIndex! ? "100%" : i === activeIndex! ? "100%" : "0%",
                    animation: i === activeIndex! ? "story-progress 20s linear" : "none",
                  }} />
              </div>
            ))}
          </div>

          <div className="relative w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <img src={current.image} alt={current.label} className="w-full h-full object-cover" />

            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />

            {/* Info overlay */}
            <div className="absolute bottom-16 right-4 left-4 z-10">
              <h3 className="font-black text-white text-xl drop-shadow-lg mb-2">{current.label}</h3>
              {current.popup_content && (
                <p className="text-sm text-white/90 leading-relaxed drop-shadow">{current.popup_content}</p>
              )}
            </div>

            {/* Close button */}
            <button onClick={closeStories}
              className="absolute top-4 left-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors cursor-pointer">
              <i className="ri-close-line text-xl"></i>
            </button>

            {/* Tap zones */}
            <div className="absolute inset-0 top-8 bottom-16 flex">
              <div className="flex-1" onClick={(e) => { e.stopPropagation(); setActiveIndex((prev) => prev !== null && prev > 0 ? prev - 1 : prev); }} />
              <div className="flex-1" onClick={(e) => { e.stopPropagation(); setActiveIndex((prev) => prev !== null && prev < stories.length - 1 ? prev + 1 : null); }} />
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes story-progress {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </section>
  );
}
